import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const SECTIONS = [
  {
    title: "Brand & Logo",
    fields: [
      { key: "logo_url", label: "Logo image URL", hint: "Paste a URL or use the upload button below", type: "image" },
      { key: "brand_name", label: "Brand name" },
      { key: "brand_tagline", label: "Brand tagline (small text under logo)" },
    ],
  },
  {
    title: "Business info",
    fields: [
      { key: "business_phone", label: "Phone (display)" },
      { key: "whatsapp_number", label: "WhatsApp number (E.164 without '+', for wa.me links)", hint: "Example: 917396871520" },
      { key: "business_address", label: "Full store address", multiline: true },
      { key: "business_hours", label: "Store hours line" },
      { key: "promo_banner_text", label: "Top promo banner text", hint: "Leave blank to hide the yellow banner at the top" },
    ],
  },
  {
    title: "Home page — Hero",
    fields: [
      { key: "trust_badge", label: "Small trust badge above heading" },
      { key: "hero_title_prefix", label: "Heading prefix", hint: "Comes before the highlighted words" },
      { key: "hero_title_highlight", label: "Heading highlighted word(s)", hint: "Shown in blue" },
      { key: "hero_title_suffix", label: "Heading suffix", hint: "Comes after the highlighted words" },
      { key: "hero_subtitle", label: "Hero subtitle paragraph", multiline: true },
      { key: "hero_stat_1_value", label: "Stat 1 value (e.g. 50+)" },
      { key: "hero_stat_1_label", label: "Stat 1 label" },
      { key: "hero_stat_2_value", label: "Stat 2 value" },
      { key: "hero_stat_2_label", label: "Stat 2 label" },
      { key: "hero_stat_3_value", label: "Stat 3 value" },
      { key: "hero_stat_3_label", label: "Stat 3 label" },
    ],
  },
  {
    title: "Footer & About",
    fields: [
      { key: "footer_tagline", label: "Footer tagline paragraph", multiline: true },
      { key: "about_intro", label: "About-page intro paragraph", multiline: true },
    ],
  },
];

export default function AdminSettings() {
  const { refresh } = useSiteSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then((r) => setForm(r.data || {}));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo_url", `${API}/files/${data.path}`);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploadingLogo(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-settings", form);
      toast.success("Site updated");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-white/50">Loading…</div>;

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl tracking-tight">Site settings</h1>
        <Button data-testid="save-settings" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-none">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Save className="h-4 w-4 mr-2"/>}
          Save changes
        </Button>
      </div>

      <p className="text-sm text-white/60 mb-6">
        Edit any text, phone number, or image below. Changes appear on the live site the moment you hit Save.
      </p>

      <div className="space-y-6">
        {SECTIONS.map((sec) => (
          <div key={sec.title} className="border border-white/10 bg-zinc-950 p-6">
            <p className="mono-label text-blue-400 mb-4">{sec.title}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {sec.fields.map((f) => (
                <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                  <Label className="text-xs text-white/70">{f.label}</Label>
                  {f.hint && <p className="text-[10px] text-white/40 font-mono mt-0.5">{f.hint}</p>}
                  {f.multiline ? (
                    <Textarea
                      data-testid={`setting-${f.key}`}
                      value={form[f.key] || ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      rows={3}
                      className="bg-black border-white/10 mt-1.5"
                    />
                  ) : (
                    <Input
                      data-testid={`setting-${f.key}`}
                      value={form[f.key] || ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      className="bg-black border-white/10 mt-1.5"
                    />
                  )}
                  {f.type === "image" && (
                    <div className="mt-2 flex items-center gap-3">
                      {form[f.key] && (
                        <img src={form[f.key]} alt="" className="h-14 w-14 rounded-md border border-white/10 object-cover bg-black"/>
                      )}
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 border border-white/20 bg-transparent hover:bg-white/5 text-sm">
                        {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Upload className="h-3.5 w-3.5"/>}
                        Upload new
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])}/>
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <Button data-testid="save-settings-bottom" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-none">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Save className="h-4 w-4 mr-2"/>}
          Save changes
        </Button>
      </div>
    </div>
  );
}
